import React from 'react';
import Proptypes from 'prop-types';
import { Route } from 'react-router-dom';
import styled from 'styled-components';
import Media from 'react-media';
import { COLORS, EXTENDS, MQS, SIZES, SPACINGS } from '../app/common/constants/styles';
import ICONS from '../app/common/constants/icons';
import { injectIntl } from 'react-intl';
import Header from '../header/Header';
import Icon from '../app/common/components/Icon';
import Logo from '../app/common/logos/RakutenLogo';
import ErrorRedirectService from '../routes/error/errorRedirect.service.ts';
import FooterContainer from '../footer/FooterContainer';
import { LoadingPage } from '../app/common/components/LoadingPage';
import { TranslationHelpers } from '../i18n/translationHelpers';
import SideBarContainer from './sideBar/SideBarContainer.tsx';
import PageWithTitle from '../app/PageWithTitle';

const LogoWrapper = styled.span`
    display: inline-block;
    width: 135px;
`;

const DefaultHeader = styled.header`
    height: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${COLORS.white};
`;

const Base = styled.div`
    background-color: ${COLORS.ultralightGrey};
`;

const DetailsWrapper = styled.div`
    display: flex;
    margin: 0 auto;
    max-width: ${SIZES.appMaxWidth}px;
    padding: 15px;
`;

const DetailsSidebar = styled.div`
    width: 30%;
    padding: ${SPACINGS.base * 3}px 0;
    position: relative;
`;

const DetailsMain = styled.div`
    width: 100%;
    filter: ${(props) => (props.blurred ? 'blur(2px)' : 'none')};

    @media (min-width: ${MQS.mq2Max}) {
        flex: 1 0 auto;
        width: 70%;
        padding-left: 20px;
    }
`;

const SectionsDropdown = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 25px;
`;

const SectionLocation = styled.div`
    display: flex;
    align-items: center;

    & span {
        margin-left: 5px;
    }
`;

const Overlay = styled.div`
    ${EXTENDS.overlayLight};
    flex-direction: column;
    justify-content: flex-end;
    padding: 0 10px 30px;
`;

const OverlayIcon = styled.span`
    align-self: flex-end;
    padding: 15px;
`;

const OverlayMenu = styled.div`
    background-color: ${COLORS.white};
    padding: 20px;
    position: relative;
    max-height: calc(100vh - 42px);
    overflow-y: auto;
`;

const MobileTitle = styled.h1`
    font-weight: 400;
    font-size: 1rem;
    margin: 0;
`;

class Details extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isOverlayOpen: false,
            sidemenu: null,
        };

        this._isMounted = true;
        this.toggleOverlay = this.toggleOverlay.bind(this);
    }

    static propTypes = {
        user: Proptypes.object.isRequired,
    };

    componentDidMount() {
        if (this.props.clientConfig.internalIdentifier === 'am') {
            import('../clients/am/SideMenu').then((comp) => {
                if (this._isMounted) {
                    this.setState({ sidemenu: comp.default });
                }
            });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    toggleOverlay() {
        this.setState((prevState) => ({
            isOverlayOpen: !prevState.isOverlayOpen,
        }));
    }

    render() {
        const { presentationConfig, clientConfig, routes, loading, intl } = this.props;

        if (loading) {
            return <LoadingPage />;
        }

        const currentPath = window.location.pathname.substring(process.env.PUBLIC_URL.length);
        const currentRouteItem =
            currentPath === '/'
                ? routes.find((route) => route.id === presentationConfig.homePage)
                : routes.find((route) => route.path === currentPath);

        if (!currentRouteItem) {
            ErrorRedirectService.notFound();
            return '';
        }
        const routesFilter = routes.filter((route) => {
            return route.isRender === undefined;
        });
        const currentRouteName = currentRouteItem.name;
        const currentRouteIcon = currentRouteItem.icon;
        const currentRouteTitle = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor(currentRouteItem.title ?? 'Rakuten Account'),
        );
        const { sidemenu: SideMenu } = this.state;

        return (
            <div>
                {SideMenu && <SideMenu />}
                {!clientConfig ? (
                    <DefaultHeader>
                        <LogoWrapper>
                            <Logo />
                        </LogoWrapper>
                    </DefaultHeader>
                ) : (
                    <Header />
                )}
                <Base>
                    <DetailsWrapper>
                        {this.state.isOverlayOpen && !presentationConfig.hideSideMenu && (
                            <Overlay>
                                <OverlayIcon onClick={this.toggleOverlay}>
                                    <Icon icon={ICONS.CLOSE} size="12" />
                                </OverlayIcon>
                                <OverlayMenu>
                                    <SideBarContainer
                                        activeRoute={currentRouteItem}
                                        routes={routesFilter}
                                    />
                                </OverlayMenu>
                            </Overlay>
                        )}
                        {!presentationConfig.hideSideMenu && (
                            <Media
                                query={`(min-width: ${MQS.mq3Min})`}
                                render={() => (
                                    <DetailsSidebar>
                                        <SideBarContainer
                                            activeRoute={currentRouteItem}
                                            routes={routesFilter}
                                        />
                                    </DetailsSidebar>
                                )}
                            />
                        )}
                        <DetailsMain blurred={this.state.isOverlayOpen}>
                            {!presentationConfig.hideSideMenu && (
                                <Media
                                    query={`(max-width: ${MQS.mq2Max})`}
                                    render={() => (
                                        <div>
                                            <SectionsDropdown
                                                onClick={this.toggleOverlay}
                                                onKeyDown={(event) => {
                                                    if (
                                                        event.key === 'Enter' ||
                                                        event.key === ' '
                                                    ) {
                                                        this.toggleOverlay();
                                                    }
                                                }}
                                                tabIndex={0}
                                            >
                                                <SectionLocation>
                                                    <Icon
                                                        icon={currentRouteIcon}
                                                        size={'26'}
                                                        viewBoxMax={'26'}
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                    />
                                                    {currentPath === '/account-security/delete' ? (
                                                        TranslationHelpers.getFormattedMessage(
                                                            currentRouteName.id,
                                                        )
                                                    ) : (
                                                        <MobileTitle>
                                                            {TranslationHelpers.getFormattedMessage(
                                                                currentRouteName.id,
                                                            )}
                                                        </MobileTitle>
                                                    )}
                                                </SectionLocation>
                                                <Icon
                                                    icon={ICONS.ARROW_DOWN}
                                                    size="20"
                                                    viewBoxMax="20"
                                                />
                                            </SectionsDropdown>
                                        </div>
                                    )}
                                />
                            )}
                            <Route
                                path={currentPath}
                                render={() => (
                                    <PageWithTitle title={currentRouteTitle}>
                                        <currentRouteItem.component />
                                    </PageWithTitle>
                                )}
                                name={currentRouteItem.name}
                            />
                        </DetailsMain>
                    </DetailsWrapper>
                    <FooterContainer></FooterContainer>
                </Base>
            </div>
        );
    }
}

export default injectIntl(Details);
